import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, AlertTriangle } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/blocks";
import {
  Field,
  TextField,
  TextAreaField,
  SelectField,
  PillGroup,
  RadioPills,
  CheckAck,
} from "@/components/FormKit";
import {
  EMIRATES,
  ARRANGEMENT_TYPES,
  LIVING_OPTIONS,
  CHILD_AGE_RANGES,
  CHILDREN_COUNT,
  PREFERRED_CONTACT,
} from "@/lib/options";

const STEPS = ["Care request", "Your needs", "Contact"];

const initialData = {
  company: "", // honeypot
  arrangement_type: "",
  living: "",
  emirate: "",
  city: "",
  start_date: "",
  schedule: "",
  number_of_children: "",
  age_ranges: [],
  experience_skills: "",
  note: "",
  contact_name: "",
  email: "",
  phone: "",
  preferred_contact: "",
  whatsapp_consent: false,
  privacy_ack: false,
};

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function RequestMatch() {
  useSeo({
    title: "Request a Nanny Match — Nanny Poppins UAE",
    description:
      "Tell us about your family and the care you're looking for. The team reviews every request personally.",
  });

  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleAge = (v) =>
    setData((d) => ({
      ...d,
      age_ranges: d.age_ranges.includes(v)
        ? d.age_ranges.filter((x) => x !== v)
        : [...d.age_ranges, v],
    }));

  const validateStep = (s) => {
    const e = {};
    if (s === 0) {
      if (!data.arrangement_type) e.arrangement_type = "Please choose an arrangement.";
      if (!data.living) e.living = "Please choose live-in or live-out.";
      if (!data.emirate) e.emirate = "Please select an Emirate.";
      if (!data.city.trim()) e.city = "Please enter your city or area.";
      if (!data.start_date.trim()) e.start_date = "Please share an ideal start date.";
      if (!data.schedule.trim()) e.schedule = "Please describe the schedule you need.";
    }
    if (s === 1) {
      if (!data.number_of_children) e.number_of_children = "Please select the number of children.";
      if (data.age_ranges.length === 0) e.age_ranges = "Please choose at least one age range.";
    }
    if (s === 2) {
      if (!data.contact_name.trim()) e.contact_name = "Please enter your name.";
      if (!emailOk(data.email)) e.email = "Please enter a valid email address.";
      if (!data.phone.trim()) e.phone = "Please enter a phone number.";
      if (!data.preferred_contact) e.preferred_contact = "Please choose a preferred contact method.";
      if (!data.privacy_ack) e.privacy_ack = "Please acknowledge the privacy notice to continue.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (validateStep(step)) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!validateStep(2)) return;
    setStatus("submitting");
    try {
      const res = await api.post("/family-requests", data);
      if (res.data?.status === "received") {
        setStatus("success");
        window.scrollTo(0, 0);
      } else {
        setStatus("error");
      }
    } catch (err) {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div>
        <PageHeader eyebrow="Request a Nanny Match" title="Thank you — your request is in." />
        <section className="container-page max-w-2xl py-16" data-testid="family-success">
          <div className="rounded-2xl border border-[#C6A04A]/40 bg-white p-10">
            <CheckCircle2 className="h-10 w-10 text-[#C6A04A]" />
            <h2 className="mt-5 font-serif text-2xl tracking-tight">Received for review</h2>
            <p className="mt-3 text-[#0B0A08]/70">
              Your request has been saved and is now with our team for review. We'll reach out using your
              preferred contact method. Nothing is booked or confirmed at this stage.
            </p>
            <Link
              to="/"
              data-testid="family-success-home"
              className="mt-8 inline-flex items-center justify-center rounded-full bg-[#0B0A08] px-7 py-3.5 text-sm font-semibold text-[#F7F4ED] hover:bg-[#0B0A08]/85"
            >
              Back to home
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Request a Nanny Match"
        title="Tell us what your family needs."
        intro="Three short steps. Every request is reviewed personally by our team — you can move back at any time without losing your answers."
      />

      <section className="container-page max-w-2xl py-14">
        {/* Progress indicator */}
        <div className="mb-10" data-testid="form-progress">
          <div className="flex items-center justify-between">
            {STEPS.map((label, i) => (
              <div key={label} className="flex flex-1 items-center">
                <div className="flex flex-col items-center gap-2">
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-full border text-sm font-semibold transition-colors ${
                      i < step
                        ? "border-[#C6A04A] bg-[#C6A04A] text-[#0B0A08]"
                        : i === step
                        ? "border-[#0B0A08] bg-[#0B0A08] text-[#F7F4ED]"
                        : "border-[#0B0A08]/20 bg-white text-[#0B0A08]/40"
                    }`}
                  >
                    {i < step ? <Check className="h-4 w-4" /> : i + 1}
                  </span>
                  <span className="hidden text-xs font-medium text-[#0B0A08]/60 sm:block">{label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`mx-2 h-px flex-1 ${i < step ? "bg-[#C6A04A]" : "bg-[#0B0A08]/15"}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {status === "error" && (
          <div
            className="mb-6 flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-700"
            data-testid="family-error"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <p>
              We couldn't save your request just now. Nothing was submitted — please check your connection and
              try again in a moment.
            </p>
          </div>
        )}

        {/* Honeypot (hidden from users) */}
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={data.company}
          onChange={(e) => set("company", e.target.value)}
          className="absolute left-[-9999px] h-0 w-0 opacity-0"
          data-testid="honeypot-company"
        />

        <div className="rounded-2xl border border-[#0B0A08]/10 bg-white p-6 sm:p-9">
          {step === 0 && (
            <div className="space-y-6" data-testid="step-1">
              <Field label="Arrangement" required error={errors.arrangement_type} htmlFor="arrangement_type">
                <RadioPills
                  id="arrangement_type"
                  options={ARRANGEMENT_TYPES}
                  value={data.arrangement_type}
                  onChange={(v) => set("arrangement_type", v)}
                />
              </Field>
              <Field label="Live-in or live-out" required error={errors.living} htmlFor="living">
                <RadioPills id="living" options={LIVING_OPTIONS} value={data.living} onChange={(v) => set("living", v)} />
              </Field>
              <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Emirate" required error={errors.emirate} htmlFor="emirate">
                  <SelectField
                    id="emirate"
                    value={data.emirate}
                    onChange={(v) => set("emirate", v)}
                    placeholder="Select Emirate"
                    options={EMIRATES}
                  />
                </Field>
                <Field label="City / area" required error={errors.city} htmlFor="city">
                  <TextField id="city" value={data.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Jumeirah" error={errors.city} />
                </Field>
              </div>
              <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Ideal start date" required error={errors.start_date} htmlFor="start_date" hint="An approximate date is fine.">
                  <TextField id="start_date" value={data.start_date} onChange={(e) => set("start_date", e.target.value)} placeholder="e.g. Next month" error={errors.start_date} />
                </Field>
                <Field label="Schedule needed" required error={errors.schedule} htmlFor="schedule">
                  <TextField id="schedule" value={data.schedule} onChange={(e) => set("schedule", e.target.value)} placeholder="e.g. Weekdays, 8am–6pm" error={errors.schedule} />
                </Field>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6" data-testid="step-2">
              <Field label="Number of children" required error={errors.number_of_children} htmlFor="number_of_children">
                <RadioPills
                  id="number_of_children"
                  options={CHILDREN_COUNT}
                  value={data.number_of_children}
                  onChange={(v) => set("number_of_children", v)}
                />
              </Field>
              <Field label="Age ranges" required error={errors.age_ranges} htmlFor="age_ranges" hint="Select all that apply.">
                <PillGroup id="age_ranges" options={CHILD_AGE_RANGES} selected={data.age_ranges} onToggle={toggleAge} />
              </Field>
              <Field
                label="Relevant experience or skills you're looking for"
                htmlFor="experience_skills"
                hint="Optional — e.g. newborn experience, help with homework, swimming."
              >
                <TextAreaField id="experience_skills" value={data.experience_skills} onChange={(e) => set("experience_skills", e.target.value)} placeholder="Tell us what matters most to your family." />
              </Field>
              <Field label="Anything else?" htmlFor="note" hint="Optional.">
                <TextAreaField id="note" value={data.note} onChange={(e) => set("note", e.target.value)} placeholder="A short note about your household or preferences." />
              </Field>
              <p className="text-xs text-[#0B0A08]/50">
                Please don't include children's full names, dates of birth or any medical details here.
              </p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="step-3">
              <Field label="Your name" required error={errors.contact_name} htmlFor="contact_name">
                <TextField id="contact_name" value={data.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Full name" error={errors.contact_name} />
              </Field>
              <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Email" required error={errors.email} htmlFor="email">
                  <TextField id="email" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" error={errors.email} />
                </Field>
                <Field label="Phone" required error={errors.phone} htmlFor="phone">
                  <TextField id="phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+971 5X XXX XXXX" error={errors.phone} />
                </Field>
              </div>
              <Field label="Preferred contact method" required error={errors.preferred_contact} htmlFor="preferred_contact">
                <RadioPills id="preferred_contact" options={PREFERRED_CONTACT} value={data.preferred_contact} onChange={(v) => set("preferred_contact", v)} />
              </Field>
              <CheckAck id="whatsapp_consent" checked={data.whatsapp_consent} onChange={(v) => set("whatsapp_consent", v)}>
                I'm happy to be contacted via WhatsApp about my request (optional).
              </CheckAck>
              <CheckAck id="privacy_ack" checked={data.privacy_ack} onChange={(v) => set("privacy_ack", v)} error={errors.privacy_ack}>
                I've read and understand the{" "}
                <Link to="/privacy-policy" className="font-medium text-[#8a6d1f] underline">
                  Privacy Policy
                </Link>{" "}
                and consent to my details being used to review my request. *
              </CheckAck>
            </div>
          )}

          {/* Nav buttons */}
          <div className="mt-9 flex items-center justify-between gap-4">
            {step > 0 ? (
              <button
                type="button"
                onClick={back}
                data-testid="form-back"
                className="inline-flex items-center gap-2 rounded-full border border-[#0B0A08]/20 px-5 py-3 text-sm font-semibold text-[#0B0A08] hover:border-[#C6A04A]"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            ) : (
              <span />
            )}

            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={next}
                data-testid="form-next"
                className="inline-flex items-center gap-2 rounded-full bg-[#0B0A08] px-6 py-3 text-sm font-semibold text-[#F7F4ED] hover:bg-[#0B0A08]/85"
              >
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={status === "submitting"}
                data-testid="family-submit"
                className="inline-flex items-center gap-2 rounded-full bg-[#C6A04A] px-6 py-3 text-sm font-semibold text-[#0B0A08] hover:scale-[1.02] disabled:opacity-60"
              >
                {status === "submitting" ? "Submitting…" : "Submit request"}
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
